import { useEffect } from "react";

const MARGIN = 8;
const BASE_STYLE = { position: "fixed", zIndex: 100 };
const TRANSFORM_ABOVE = "translateX(-50%) translateY(calc(-100% - 8px))";
const TRANSFORM_BELOW = "translateX(-50%) translateY(8px)";

/**
 * Keeps a fixed-position element anchored below (or above) a viewport rect,
 * switching sides based on available space. Also returns the correct style
 * for the first render so there is no flicker.
 *
 * @param {React.RefObject} elementRef - ref to the outer div being positioned
 * @param {Function|null} getViewportRect - live rect callback; RAF stops if null
 * @param {Object|null} selectionRect - static initial rect for the first paint
 * @param {number} heightEstimate - fallback height before the element is measured
 * @returns {{ style: object }}
 */
export default function useAnchoredToRect(
  elementRef,
  getViewportRect,
  selectionRect,
  heightEstimate,
) {
  useEffect(() => {
    if (!getViewportRect) return;

    let requestAnimationFrameId;
    const update = () => {
      if (elementRef.current) {
        const rect = getViewportRect();
        const height = elementRef.current.offsetHeight || heightEstimate;
        const showAbove = rect.top > height + MARGIN;
        elementRef.current.style.transform = showAbove
          ? TRANSFORM_ABOVE
          : TRANSFORM_BELOW;
        elementRef.current.style.left = `${rect.left + rect.width / 2}px`;
        elementRef.current.style.top = showAbove
          ? `${rect.top}px`
          : `${rect.top + rect.height}px`;
      }
      requestAnimationFrameId = requestAnimationFrame(update);
    };
    requestAnimationFrameId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(requestAnimationFrameId);
  }, [getViewportRect]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!selectionRect) {
    return {
      style: {
        ...BASE_STYLE,
        top: "50%",
        left: "50%",
        transform: "translateX(-50%) translateY(-50%)",
      },
    };
  }

  const showAbove = selectionRect.top > heightEstimate + MARGIN;
  return {
    style: {
      ...BASE_STYLE,
      top: showAbove
        ? selectionRect.top
        : selectionRect.top + selectionRect.height,
      left: selectionRect.left + selectionRect.width / 2,
      transform: showAbove ? TRANSFORM_ABOVE : TRANSFORM_BELOW,
    },
  };
}
